import { debounce } from 'underscore';
import React, { useEffect, useRef, useState } from 'react';

import { Game } from '../Game';
import * as Types from './types';
import * as hooks from './hooks';
import * as helpers from './helpers';
import { categorySelector } from '../../store/helpers';
import { useDynamicReducerSelector } from '../../store';

import style from './GamesRow.scss';

export const GamesRow: React.FC<Types.IGamesRowProps> = ({ games, categoryName, tabIndex, isLastItem }) => {
    const rowContainer = useRef<HTMLDivElement>(null);
    const [isRowVisible, setIsRowVisible] = useState<boolean>(false);
    const category = useDynamicReducerSelector(categorySelector(categoryName));
    const { categoryGameGridLayout, categoryView } = category?.categoryConfiguration || {};
    const resizeGamesTiles = hooks.useResizeGameTiles({
        containerRef: rowContainer, categoryGameGridLayout, categoryView, isLastItem
    });
    const gamesRatios = helpers.getGamesRatios(categoryGameGridLayout);

    useEffect(() => {
        resizeGamesTiles();
    }, [resizeGamesTiles]);

    useEffect(() => {
        const element = rowContainer.current;
        const debouncedResizeGamesTiles = debounce(() => {
            resizeGamesTiles();
            window.scrollTo(0, window.scrollY);
        }, 100);
        // applying throttle for performance increase
        const resizeObserver = new ResizeObserver(() => {
            debouncedResizeGamesTiles();
        });

        element && resizeObserver.observe(element);

        return () => {
            element && resizeObserver.unobserve(element);
        };
    }, [resizeGamesTiles]);

    useEffect(() => {
        const element = rowContainer.current;
        const observer = new IntersectionObserver(helpers.intersectionObserverHandler(setIsRowVisible), { rootMargin: '300px' });

        element && observer.observe(element);

        return () => {
            element && observer.unobserve(element);
        };
    }, [setIsRowVisible]);

    return (
        <div tabIndex={tabIndex} ref={rowContainer} className={`${style.gamesRow} cms-games-grid-games-row`}>
            {isRowVisible && categoryGameGridLayout?.layoutTiles.map((tile, index) => {
                return games[index] ? (
                    <Game
                        gridArea={tile}
                        gameCode={games[index]}
                        ratio={gamesRatios[tile]}
                        size={categoryGameGridLayout.tileSize}
                        key={`${categoryName}-${games[index]}`}
                        borderRadius={`${categoryGameGridLayout.tileRadius || 0}px`}
                    />
                ) : null;
            })}
        </div>
    );
};

export default GamesRow;
